import { useQuery } from '@tanstack/react-query'
import { User } from '@xeetr/types/models'
import { useContext, useEffect } from 'react'

import { ActiveTenantContext, ActiveUserContext } from '../context/common/Context'
import { queryKeys } from '../lib/queryKeys'
import { UserService } from '../services/User.service'
import { clearAutoLogout } from '../utils/autoLogout'

import { useErrorHandler } from './common/useErrorHandler'
import { useGlobalService } from './common/useServiceFactory'

interface UseUserReturn {
  user: User | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Modern hook for managing user data with automatic context synchronization.
 *
 * Features:
 * - Fetches authenticated user data
 * - Automatically syncs user to ActiveUserContext
 * - Handles tenant selection from sessionStorage
 * - Shows error toasts on failure
 * - React Query caching with 10-minute stale time
 *
 * @example
 * ```tsx
 * const { user, isLoading, isError } = useUser()
 * ```
 */
export const useUser = (): UseUserReturn => {
  const { setUser } = useContext(ActiveUserContext)
  const { tenantId, setTenantId } = useContext(ActiveTenantContext)

  const getService = useGlobalService(UserService)

  const userQuery = useQuery<User>({
    queryKey: queryKeys.user(),
    queryFn: async () => {
      const service = await getService()
      return service.getUser()
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (user data changes infrequently)
    retry: false, // Don't retry on failure, show error immediately
  })

  useErrorHandler(userQuery.isError, 'Fehler beim Laden des Benutzers.')

  useEffect(() => {
    if (userQuery.data && !tenantId) {
      const session = sessionStorage.getItem('tenantId')
      const user = userQuery.data

      if (session && user.tenants.find((t) => t.tenantId === session)) {
        setTenantId(session)
      } else {
        setTenantId(user.defaultTenant)
      }
    }
  }, [userQuery.data, tenantId, setTenantId])

  useEffect(() => {
    if (userQuery.data) {
      setUser(userQuery.data)
      // The session works again — a future rejected token may auto logout once more.
      clearAutoLogout()
    }
  }, [userQuery.data, setUser])

  return {
    user: userQuery.data,
    isLoading: userQuery.isLoading,
    isError: userQuery.isError,
    error: userQuery.error,
    refetch: userQuery.refetch,
  }
}
